import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  model,
  signal
} from '@angular/core';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, finalize, switchMap, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Listbox } from 'primeng/listbox';
import { ImpersonationService } from '../../services/impersonation.service';
import { ImpersonationUser } from '../../models';

@Component({
  selector: 'app-impersonation-dialog',
  imports: [ReactiveFormsModule, Dialog, Button, InputText, ProgressSpinner, Listbox],
  templateUrl: './impersonation-dialog.component.html',
  styleUrl: './impersonation-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImpersonationDialogComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly impersonationService = inject(ImpersonationService);

  visible = model<boolean>(false);

  readonly searchControl = new FormControl('', { nonNullable: true });
  readonly selectedControl = new FormControl<ImpersonationUser | null>(null);
  readonly selectedUser = toSignal(this.selectedControl.valueChanges, { initialValue: null });

  readonly results = signal<ImpersonationUser[]>([]);
  readonly loading = signal(false);
  readonly error = signal('');
  readonly searched = signal(false);

  constructor() {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(400),
        switchMap(q => {
          const trimmed = q.trim();
          if (trimmed.length < 2) {
            this.results.set([]);
            this.searched.set(false);
            this.error.set('');
            return of([]);
          }
          this.loading.set(true);
          this.error.set('');
          this.results.set([]);
          this.searched.set(false);
          this.selectedControl.setValue(null, { emitEvent: false });
          return this.impersonationService.searchUsers(trimmed).pipe(
            catchError(() => {
              this.error.set('Failed to search users. Please try again.');
              return of([]);
            }),
            finalize(() => {
              this.loading.set(false);
              this.searched.set(true);
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(users => this.results.set(users));
  }

  onConfirm(): void {
    const user = this.selectedControl.value;
    if (!user) return;
    this.impersonationService.startImpersonation(user);
    this.close();
  }

  onCancel(): void {
    this.close();
  }

  private close(): void {
    this.visible.set(false);
    this.searchControl.reset();
    this.results.set([]);
    this.selectedControl.setValue(null, { emitEvent: false });
    this.error.set('');
    this.searched.set(false);
  }
}
